/**
 * Hulasi Transliterate — Offline Latin (Hinglish) to Devanagari
 * Zero dependencies, no API calls.
 *
 * Usage:
 *   window.HulasiTransliterate.toHindi('namaste') → 'नमस्ते'
 *   window.HulasiTransliterate.toHindi('rahul')   → 'राहुल'
 *
 * Approach:
 *   Greedy longest-match transliteration using a curated phonetic mapping.
 *   Handles consonant clusters, implicit 'a' (schwa), matras, and nukta.
 *
 * License: MIT (custom-built for Hulasi)
 */

(function () {
  'use strict';

  /* ================================================================
     DEVANAGARI COMPONENTS
     ================================================================ */

  // Vowels (standalone form)
  const VOWELS = {
    'a': '\u0905',      // अ
    'aa': '\u0906',     // आ
    'i': '\u0907',      // इ
    'ee': '\u0908',     // ई
    'ii': '\u0908',     // ई
    'u': '\u0909',      // उ
    'oo': '\u090A',     // ऊ
    'uu': '\u090A',     // ऊ
    'e': '\u090F',      // ए
    'ai': '\u0910',     // ऐ
    'o': '\u0913',      // ओ
    'au': '\u0914',     // औ
    'ri': '\u090B',     // ऋ
  };

  // Vowel signs (matra — used after consonants)
  const MATRAS = {
    'a': '',            // inherent, no matra
    'aa': '\u093E',     // ा
    'i': '\u093F',      // ि
    'ee': '\u0940',     // ी
    'ii': '\u0940',     // ी
    'u': '\u0941',      // ु
    'oo': '\u0942',     // ू
    'uu': '\u0942',     // ू
    'e': '\u0947',      // े
    'ai': '\u0948',     // ै
    'o': '\u094B',      // ो
    'au': '\u094C',     // ौ
    'ri': '\u0943',     // ृ
  };

  // Consonants
  const CONSONANTS = {
    'k': '\u0915',      // क
    'kh': '\u0916',     // ख
    'g': '\u0917',      // ग
    'gh': '\u0918',     // घ
    'ng': '\u0919',     // ङ

    'ch': '\u091A',     // च
    'chh': '\u091B',    // छ
    'j': '\u091C',      // ज
    'jh': '\u091D',     // झ
    'ny': '\u091E',     // ञ

    't': '\u0924',      // त
    'th': '\u0925',     // थ
    'd': '\u0926',      // द
    'dh': '\u0927',     // ध
    'n': '\u0928',      // न

    'p': '\u092A',      // प
    'ph': '\u092B',     // फ
    'f': '\u092B',      // फ (alias)
    'b': '\u092C',      // ब
    'bh': '\u092D',     // भ
    'm': '\u092E',      // म

    'y': '\u092F',      // य
    'r': '\u0930',      // र
    'l': '\u0932',      // ल
    'v': '\u0935',      // व
    'w': '\u0935',      // व (alias)

    'sh': '\u0936',     // श
    'shh': '\u0937',    // ष
    's': '\u0938',      // स
    'h': '\u0939',      // ह

    // Retroflex consonants (capital convention or double)
    'T': '\u091F',      // ट
    'Th': '\u0920',     // ठ
    'D': '\u0921',      // ड
    'Dh': '\u0922',     // ढ
    'N': '\u0923',      // ण

    // Common Hinglish patterns
    'q': '\u0915\u093C', // क़
    'x': '\u0915\u094D\u0938', // क्स
    'z': '\u091C\u093C', // ज़
  };

  // Special / conjunct patterns (common Hinglish shortcuts)
  const SPECIAL = {
    'ksh': '\u0915\u094D\u0937',  // क्ष
    'gy': '\u091C\u094D\u091E',   // ज्ञ (gya/gyan)
    'gn': '\u091C\u094D\u091E',   // ज्ञ alias
    'tr': '\u0924\u094D\u0930',   // त्र
    'shr': '\u0936\u094D\u0930',  // श्र
    'kr': '\u0915\u094D\u0930',   // क्र
    'pr': '\u092A\u094D\u0930',   // प्र
    'dr': '\u0926\u094D\u0930',   // द्र
    'gr': '\u0917\u094D\u0930',   // ग्र
    'br': '\u092C\u094D\u0930',   // ब्र
    'mr': '\u092E\u094D\u0930',   // म्र
    'nr': '\u0928\u094D\u0930',   // न्र
    'nk': '\u0902\u0915',         // ंक
    'nd': '\u0928\u094D\u0926',   // न्द
    'nt': '\u0928\u094D\u0924',   // न्त
    'mp': '\u092E\u094D\u092A',   // म्प
    'mb': '\u092E\u094D\u092C',   // म्ब
  };

  // Halant (virama) — suppresses inherent 'a'
  const HALANT = '\u094D';

  // Anusvara (nasal dot)
  const ANUSVARA = '\u0902';

  // Visarga
  const VISARGA = '\u0903';

  // Nukta
  const NUKTA = '\u093C';

  /* ================================================================
     BUILD SORTED KEY ARRAYS (longest first for greedy matching)
     ================================================================ */

  const allConsonantKeys = Object.keys(CONSONANTS).sort((a, b) => b.length - a.length);
  const allVowelKeys = Object.keys(VOWELS).sort((a, b) => b.length - a.length);
  const allSpecialKeys = Object.keys(SPECIAL).sort((a, b) => b.length - a.length);

  /* ================================================================
     HELPER: Check if a string at position starts with a key
     ================================================================ */

  function matchAtPosition(text, pos, keys, map) {
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (text.substring(pos, pos + key.length).toLowerCase() === key.toLowerCase() ||
          text.substring(pos, pos + key.length) === key) {
        return { key: key, value: map[key], length: key.length };
      }
    }
    return null;
  }

  function matchExact(text, pos, keys, map) {
    // Case-sensitive check first (for T, Th, D, Dh, N)
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const substr = text.substring(pos, pos + key.length);
      if (substr === key) {
        return { key: key, value: map[key], length: key.length };
      }
    }
    // Then case-insensitive
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      // Skip uppercase-only keys for case-insensitive (they are special retroflex)
      if (key[0] === key[0].toUpperCase() && key[0] !== key[0].toLowerCase()) continue;
      const substr = text.substring(pos, pos + key.length).toLowerCase();
      if (substr === key.toLowerCase()) {
        return { key: key, value: map[key], length: key.length };
      }
    }
    return null;
  }

  /* ================================================================
     MAIN TRANSLITERATION FUNCTION
     ================================================================ */

  function toHindi(input) {
    if (!input || typeof input !== 'string') return '';

    const text = input;
    let result = '';
    let i = 0;
    let prevWasConsonant = false;

    while (i < text.length) {
      const ch = text[i];

      // Skip spaces and non-alpha characters — pass through
      if (ch === ' ') {
        result += ' ';
        i++;
        prevWasConsonant = false;
        continue;
      }

      if (!/[a-zA-Z]/.test(ch)) {
        result += ch;
        i++;
        prevWasConsonant = false;
        continue;
      }

      // 1. Try special conjuncts first (longest match)
      let specialMatch = null;
      for (let s = 0; s < allSpecialKeys.length; s++) {
        const key = allSpecialKeys[s];
        const substr = text.substring(i, i + key.length);
        if (substr.toLowerCase() === key.toLowerCase()) {
          specialMatch = { key: key, value: SPECIAL[key], length: key.length };
          break;
        }
      }

      if (specialMatch) {
        // Check if followed by a vowel
        const afterSpecial = i + specialMatch.length;
        const vowelAfter = matchVowelAt(text, afterSpecial);

        if (prevWasConsonant) {
          // Add halant before this conjunct if previous was consonant
          result += HALANT;
        }

        result += specialMatch.value;

        if (vowelAfter) {
          if (vowelAfter.key !== 'a') {
            result += MATRAS[vowelAfter.key] || '';
          }
          i = afterSpecial + vowelAfter.length;
        } else {
          // Check if next char is a consonant or end — if end, add inherent 'a' schwa
          const nextCharAfter = text[afterSpecial];
          if (!nextCharAfter || nextCharAfter === ' ' || !/[a-zA-Z]/.test(nextCharAfter)) {
            // End of word — typically drop schwa in Hindi, but for names keep it simple
            // We'll add halant only if next is consonant
          }
          i = afterSpecial;
        }
        prevWasConsonant = true;
        continue;
      }

      // 2. Try consonant
      const consonantMatch = matchExact(text, i, allConsonantKeys, CONSONANTS);

      if (consonantMatch) {
        const afterConsonant = i + consonantMatch.length;

        // Handle double consonant (e.g., 'kk' → क्क)
        // Check if same consonant repeats
        const nextConsonant = matchExact(text, afterConsonant, allConsonantKeys, CONSONANTS);

        if (prevWasConsonant) {
          result += HALANT;
        }

        result += consonantMatch.value;

        // Check for following vowel
        const vowelAfter = matchVowelAt(text, afterConsonant);

        if (vowelAfter) {
          if (vowelAfter.key !== 'a') {
            result += MATRAS[vowelAfter.key] || '';
          }
          // 'a' is inherent — no matra needed
          i = afterConsonant + vowelAfter.length;
          prevWasConsonant = false;
        } else {
          i = afterConsonant;
          prevWasConsonant = true;
        }
        continue;
      }

      // 3. Try standalone vowel
      const vowelMatch = matchVowelAt(text, i);

      if (vowelMatch) {
        if (prevWasConsonant) {
          // This vowel modifies previous consonant
          if (vowelMatch.key !== 'a') {
            result += MATRAS[vowelMatch.key] || '';
          }
          prevWasConsonant = false;
        } else {
          // Standalone vowel
          result += vowelMatch.value;
          prevWasConsonant = false;
        }
        i += vowelMatch.length;
        continue;
      }

      // 4. Handle 'n' before certain consonants as anusvara
      if (ch.toLowerCase() === 'n' && i + 1 < text.length) {
        const nextCh = text[i + 1].toLowerCase();
        if ('kgcjdtpb'.includes(nextCh)) {
          if (prevWasConsonant) result += HALANT;
          result += ANUSVARA;
          i++;
          prevWasConsonant = false;
          continue;
        }
      }

      // 5. Fallback — unknown character, pass through
      result += ch;
      i++;
      prevWasConsonant = false;
    }

    // If the string ends with a consonant and no vowel follows,
    // the inherent 'a' is usually dropped in Hindi (schwa deletion).
    // For names, we'll apply a simple schwa deletion rule:
    // Remove trailing inherent 'a' for words > 2 syllables
    // For simplicity in names, keep as-is (most names sound natural)

    return result;
  }

  function matchVowelAt(text, pos) {
    if (pos >= text.length) return null;

    // Sort by length descending for greedy match
    for (let v = 0; v < allVowelKeys.length; v++) {
      const key = allVowelKeys[v];
      const substr = text.substring(pos, pos + key.length).toLowerCase();
      if (substr === key) {
        return { key: key, value: VOWELS[key], length: key.length };
      }
    }
    return null;
  }

  /* ================================================================
     COMMON NAME OVERRIDES (for better accuracy on popular names)
     ================================================================ */

  const NAME_OVERRIDES = {
    'rahul': 'राहुल',
    'priya': 'प्रिया',
    'amit': 'अमित',
    'anita': 'अनिता',
    'suresh': 'सुरेश',
    'ramesh': 'रमेश',
    'pooja': 'पूजा',
    'puja': 'पूजा',
    'neha': 'नेहा',
    'arun': 'अरुण',
    'sunita': 'सुनीता',
    'deepak': 'दीपक',
    'dipak': 'दीपक',
    'ravi': 'रवि',
    'sanjay': 'संजय',
    'vijay': 'विजय',
    'ajay': 'अजय',
    'meena': 'मीना',
    'mina': 'मीना',
    'krishna': 'कृष्णा',
    'ganesh': 'गणेश',
    'mahesh': 'महेश',
    'manish': 'मनीष',
    'rohit': 'रोहित',
    'mohit': 'मोहित',
    'nitin': 'नितिन',
    'sachin': 'सचिन',
    'tushar': 'तुषार',
    'kiran': 'किरण',
    'naman': 'नमन',
    'aman': 'अमन',
    'rohan': 'रोहन',
    'sohan': 'सोहन',
    'mohan': 'मोहन',
    'arjun': 'अर्जुन',
    'varun': 'वरुण',
    'tarun': 'तरुण',
    'anil': 'अनिल',
    'sunil': 'सुनील',
    'kapil': 'कपिल',
    'gopal': 'गोपाल',
    'vishal': 'विशाल',
    'raj': 'राज',
    'simran': 'सिमरन',
    'gurpreet': 'गुरप्रीत',
    'harpreet': 'हरप्रीत',
    'naveen': 'नवीन',
    'navin': 'नवीन',
    'praveen': 'प्रवीण',
    'pravin': 'प्रवीण',
    'ankita': 'अंकिता',
    'ankit': 'अंकित',
    'swati': 'स्वाति',
    'shweta': 'श्वेता',
    'kavita': 'कविता',
    'savita': 'सविता',
    'namaste': 'नमस्ते',
    'hulasi': 'हुलसी',
  };

  /**
   * Main public function with override support
   */
  function transliterate(input) {
    if (!input || typeof input !== 'string') return '';

    const trimmed = input.trim();
    if (trimmed.length === 0) return '';

    // Check for exact name override (case-insensitive)
    const lowerTrimmed = trimmed.toLowerCase();
    if (NAME_OVERRIDES[lowerTrimmed]) {
      return NAME_OVERRIDES[lowerTrimmed];
    }

    // Multi-word: check each word for overrides
    const words = trimmed.split(/\s+/);
    if (words.length > 1) {
      const transliteratedWords = words.map((word) => {
        const lower = word.toLowerCase();
        if (NAME_OVERRIDES[lower]) return NAME_OVERRIDES[lower];
        return toHindi(word);
      });
      return transliteratedWords.join(' ');
    }

    return toHindi(trimmed);
  }

  /* ================================================================
     EXPORT
     ================================================================ */

  window.HulasiTransliterate = {
    toHindi: transliterate,
    // Expose raw function for testing
    _toHindiRaw: toHindi,
    _overrides: NAME_OVERRIDES,
  };

})();